import { useState } from 'react'
import {
  useCoordinationDisposition,
  useCoordinationFindings,
  useProtectionTree,
} from '../../hooks/useFaultAnalysis.ts'
import { COORDINATION_COLORS, type ProtectionCoordinationPair } from '../../types/faultAnalysis.ts'
import { DISPOSITION_LABELS, type Disposition } from '../../types/tcc.ts'
import { groupBlockedByReason, splitPairsByBasis } from './protectionGrouping.ts'
import { TccViewer } from './TccViewer.tsx'
import { exportCsv } from '../../utils/csv.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { IndicativeBanner, INDICATIVE_VALUE_COLOR, SetupDataChip } from './indicative.tsx'
import { Term } from '../common/Term.tsx'
import { AssumptionsDisclosure } from './AssumptionsDisclosure.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface ProtectionTreeProps {
  feederId: number
}

const STATUS_LABELS: Record<string, string> = {
  coordinated: 'Coordinated',
  marginal: 'Marginal',
  uncoordinated: 'Uncoordinated',
  requires_tcc_review: 'Requires TCC review',
}

const DISPOSITIONS: Disposition[] = ['open', 'acknowledged', 'field_corrected']

/** The per-pair operator disposition control — one implementation shared by
 *  evaluated table rows and blocked-group rows so the two never drift. */
function DispositionSelect({
  pair,
  value,
  onChange,
}: {
  pair: ProtectionCoordinationPair
  value: string
  onChange: (d: Disposition) => void
}) {
  const pairKey = `${pair.upstream_device_id}:${pair.downstream_device_id}`
  return (
    <select
      aria-label={`Disposition for pair ${pairKey}`}
      className="border border-gray-200 rounded px-1 py-0.5 text-xs"
      value={value}
      onChange={e => onChange(e.target.value as Disposition)}
    >
      {DISPOSITIONS.map(d => (
        <option key={d} value={d}>{DISPOSITION_LABELS[d]}</option>
      ))}
    </select>
  )
}

/** A protection-topology job warning (from build_protection_tree) that names the
 *  script is the "job never ran" signal — surfaced distinctly, never as an empty
 *  clean bill (spec §9 degraded-explicit). */
function isJobNeverRan(warnings: string[]): boolean {
  return warnings.some(w => w.includes('compute_protection_topology'))
}

export function ProtectionTree({ feederId }: ProtectionTreeProps) {
  const { data, isLoading, error } = useProtectionTree(feederId)
  const { data: findings } = useCoordinationFindings(feederId)
  const disposition = useCoordinationDisposition(feederId)
  const [curvesFor, setCurvesFor] = useState<{ up: number; dn: number } | null>(null)

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Building protection tree...</div>
  }
  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">
        {friendlyError(error, 'load the protection tree')} This is a request
        error, not an empty feeder.
      </div>
    )
  }
  if (!data) {
    return <div className="text-sm text-gray-400 p-4">No protection data available</div>
  }

  const jobNeverRan = isJobNeverRan(data.warnings)
  // C2: hard-gated pairs are an engineer worklist — exportable as CSV.
  const blockedPairs = data.pairs.filter(p => p.coordination_basis === 'cannot_assess')
  const dispositionByPair = new Map(
    (findings?.findings ?? []).map(f => [
      `${f.upstream_device_id}:${f.downstream_device_id}`, f.disposition,
    ]),
  )
  // C2 (UX Stage 1): un-assessable pairs never interleave with evaluated rows —
  // they render below, grouped by blocking reason. Null-basis (legacy) pairs
  // stay in the table; see protectionGrouping.ts.
  const { evaluated, blocked } = splitPairsByBasis(data.pairs)
  const blockedGroups = groupBlockedByReason(blocked)
  const toggleCurves = (p: ProtectionCoordinationPair) =>
    setCurvesFor(prev =>
      prev?.up === p.upstream_device_id && prev?.dn === p.downstream_device_id
        ? null
        : { up: p.upstream_device_id, dn: p.downstream_device_id },
    )
  const saveDisposition = (p: ProtectionCoordinationPair, d: Disposition) =>
    disposition.mutate({
      feeder_id: feederId,
      upstream_device_id: p.upstream_device_id,
      downstream_device_id: p.downstream_device_id,
      disposition: d,
    })
  // A1 demotion: grey counts/magnitudes and neutralise status chips while
  // indicative (indicative_only is optional on this result type).
  const indicative = data.indicative_only === true
  const muted = indicative ? ' indicative-muted' : ''

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Protection Coordination
          {indicative && <SetupDataChip />}
        </h3>
        <div className="flex items-center gap-2">
          {blockedPairs.length > 0 && (
            <button
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={() =>
                exportCsv(
                  `blocked-coordination-feeder-${feederId}.csv`,
                  blockedPairs.map(p => ({
                    upstream_id: p.upstream_device_id,
                    upstream: p.upstream_device_name ?? '',
                    upstream_type: p.upstream_device_type,
                    downstream_id: p.downstream_device_id,
                    downstream: p.downstream_device_name ?? '',
                    downstream_type: p.downstream_device_type,
                    blocking_reason: p.notes ?? '',
                  })),
                )
              }
            >
              Export blocked pairs (CSV)
            </button>
          )}
          <span className="text-xs text-gray-500">{data.total_devices} devices</span>
        </div>
      </div>
      <AsOfStamp computedAt={data.analysis_timestamp} />

      {indicative && <IndicativeBanner />}

      {jobNeverRan && (
        <div
          className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-800"
          title="Engineering job: scripts/compute_protection_topology.py"
        >
          <span className="font-semibold">Protection topology not computed.</span>{' '}
          No device has an upstream link yet — this feeder needs an engineering
          data load; flag for the engineer. Coordination pairs cannot be derived
          until it has run (this is not a clean bill).
        </div>
      )}

      {/* Summary badges */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div
            className="text-2xl font-bold"
            style={{ color: indicative ? INDICATIVE_VALUE_COLOR : COORDINATION_COLORS.coordinated }}
          >
            {data.coordinated_pairs}
          </div>
          <div className="text-xs text-gray-500 mt-1">Coordinated</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div
            className="text-2xl font-bold"
            style={{ color: indicative ? INDICATIVE_VALUE_COLOR : COORDINATION_COLORS.uncoordinated }}
          >
            {data.flagged_pairs}
          </div>
          <div className="text-xs text-gray-500 mt-1">Flagged</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
          <div
            className="text-2xl font-bold"
            style={{ color: indicative ? INDICATIVE_VALUE_COLOR : COORDINATION_COLORS.requires_tcc_review }}
          >
            {data.tcc_review_needed}
          </div>
          <div className="text-xs text-gray-500 mt-1"><Term k="tcc">TCC review</Term></div>
        </div>
      </div>

      {/* Non-job warnings render once per tab via WarningSummary (spec §3.1).
          The "Protection topology not computed" banner above stays — it is a
          degraded-explicit state, not a warning wall (exempt per spec §3.1). */}

      {/* A3 (spec §3.5): the modelling assumptions this run actually used. */}
      <AssumptionsDisclosure assumptions={data.assumptions ?? []} />

      {evaluated.length > 0 ? (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="pb-1 pr-2">Upstream</th>
                <th className="pb-1 pr-2">Downstream</th>
                <th className="pb-1 pr-2">Status</th>
                <th className="pb-1 pr-2">Fault (kA)</th>
                <th className="pb-1 pr-2">Margin (TC / MM s)</th>
                <th className="pb-1 pr-2">Notes</th>
                <th className="pb-1 pr-2">Disposition</th>
                <th className="pb-1">Curves</th>
              </tr>
            </thead>
            <tbody>
              {evaluated.map((p, i) => {
                const color = indicative
                  ? INDICATIVE_VALUE_COLOR
                  : COORDINATION_COLORS[p.coordination_status] || '#6b7280'
                const tcc = p.coordination_status === 'requires_tcc_review'
                const pairKey = `${p.upstream_device_id}:${p.downstream_device_id}`
                const fuseFuse =
                  p.upstream_device_type === 'fuse' && p.downstream_device_type === 'fuse'
                return (
                  <tr key={i} className={`border-b border-gray-50 ${tcc ? 'bg-blue-50' : ''}`}>
                    <td className="py-1 pr-2 text-gray-700">
                      <div className="font-medium">{p.upstream_device_name || `#${p.upstream_device_id}`}</div>
                      <div className="text-gray-400 capitalize">{p.upstream_device_type.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="py-1 pr-2 text-gray-700">
                      <div className="font-medium">{p.downstream_device_name || `#${p.downstream_device_id}`}</div>
                      <div className="text-gray-400 capitalize">{p.downstream_device_type.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="py-1 pr-2">
                      <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
                            style={{ backgroundColor: color + '20', color }}>
                        {STATUS_LABELS[p.coordination_status] || p.coordination_status}
                      </span>
                    </td>
                    <td className={`py-1 pr-2 text-gray-700${muted}`}>
                      {p.fault_current_ka != null ? p.fault_current_ka.toFixed(2) : '—'}
                    </td>
                    <td className={`py-1 pr-2 text-gray-700${muted}`}>
                      {p.tc_downstream_s != null && p.mm_upstream_s != null
                        ? `${p.tc_downstream_s} / ${p.mm_upstream_s}`
                        : '—'}
                    </td>
                    <td className="py-1 pr-2 text-gray-500">{p.notes || '—'}</td>
                    <td className="py-1 pr-2">
                      <DispositionSelect
                        pair={p}
                        value={dispositionByPair.get(pairKey) ?? 'open'}
                        onChange={d => saveDisposition(p, d)}
                      />
                    </td>
                    <td className="py-1">
                      {fuseFuse && (
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          onClick={() => toggleCurves(p)}
                        >
                          Curves
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        !jobNeverRan && blocked.length === 0 && (
          <div className="text-sm text-gray-400 p-2">
            No coordination pairs (feeder head devices only, or nothing to coordinate).
          </div>
        )
      )}

      {blockedGroups.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Cannot assess yet ({blocked.length})
          </h4>
          <p className="text-xs text-gray-500">
            These device pairs can't be checked automatically yet — the reason
            is listed on each group. They are not failures; they need data or
            an engineer's review.
          </p>
          {blockedGroups.map(g => (
            <details key={g.reason} className="rounded border border-gray-200 bg-gray-50">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-gray-700">
                {`${g.pairs.length} ${g.pairs.length === 1 ? 'pair' : 'pairs'} — ${g.reason}`}
              </summary>
              <div className="px-3 pb-2 space-y-1">
                {g.pairs.map(p => {
                  const pairKey = `${p.upstream_device_id}:${p.downstream_device_id}`
                  const fuseFuse =
                    p.upstream_device_type === 'fuse' && p.downstream_device_type === 'fuse'
                  return (
                    <div key={pairKey} className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
                      <span className="font-medium">
                        {p.upstream_device_name || `#${p.upstream_device_id}`}
                      </span>
                      <span className="text-gray-400">&rarr;</span>
                      <span className="font-medium">
                        {p.downstream_device_name || `#${p.downstream_device_id}`}
                      </span>
                      <span className="text-gray-400 capitalize">
                        ({p.upstream_device_type.replace(/_/g, ' ')} &rarr;{' '}
                        {p.downstream_device_type.replace(/_/g, ' ')})
                      </span>
                      <DispositionSelect
                        pair={p}
                        value={dispositionByPair.get(pairKey) ?? 'open'}
                        onChange={d => saveDisposition(p, d)}
                      />
                      {fuseFuse && (
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                          onClick={() => toggleCurves(p)}
                        >
                          Curves
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            </details>
          ))}
        </div>
      )}

      {disposition.isError && (
        <div className="text-xs text-red-600">
          Failed to save disposition — refresh and retry.
        </div>
      )}

      {curvesFor && (
        <div className="border border-gray-200 rounded-lg p-3">
          <TccViewer
            feederId={feederId}
            upstreamDeviceId={curvesFor.up}
            downstreamDeviceId={curvesFor.dn}
            onClose={() => setCurvesFor(null)}
          />
        </div>
      )}
    </div>
  )
}
